// =============================================================================
// client/src/lib/flux/board.js | v1.0 | SQ-Chrome S1 | 2026-08-27
// =============================================================================
// The FluxEngine "board" design vocabulary, in ONE place.
//
// FluxEngine is a product inside the Front Office and reads as its own thing:
// hard 2px ink outlines, a ZERO-BLUR offset ledge shadow, dashed perforation
// dividers, a heavy left spine on cards, uppercase pill controls, and a paper
// ground. It is deliberately NOT the Front Office's soft dashboard — that
// contrast is the seam that tells an operator which product they are in.
//
// 🔴 WHY ARBITRARY HEX AND NOT tailwind.config TOKENS:
// tailwind.config.js does NOT hot-reload in this setup. Proven at SQ-Chrome S1
// with getComputedStyle — newly added config tokens resolved to
// `rgba(0, 0, 0, 0)` and `animationName: none` with ZERO matching CSS rules,
// while arbitrary values on the same page compiled fine. Config colours cost a
// dev-server restart per change; arbitrary values compile on save. Until that
// is understood and fixed, this file uses arbitrary values on purpose.
// Do not "clean this up" into config tokens without re-testing the reload.
//
// USAGE — import what you need, compose with template strings:
//   import { PANEL, DASH, BTN_DARK, INK } from '../../lib/flux/board.js';
//   <section className={PANEL}> … </section>
//   <button className={BTN_DARK}>New Campaign</button>
// =============================================================================

// Ink is hunter-deep. Muted is the second of the two type inks — this system
// has exactly two, and adding a third is how it turns to mud.
export const INK = '#0E2A20';
export const MUTED = '#5A5A5A';
export const PAPER = '#F4F4F2';

// Page ground. Every FluxEngine route gets this on its outermost element.
export const PAGE = 'min-h-screen bg-[#F4F4F2]';

// Panels. PANEL carries the ledge; PANEL_QUIET is the same box without it, for
// containers that hold rows rather than announce themselves.
export const PANEL = 'bg-white border-2 border-[#0E2A20] rounded-2xl shadow-[0_4px_0_#0E2A20]';
export const PANEL_QUIET = 'bg-white border-2 border-[#0E2A20] rounded-2xl';

// The perforation. Used as a divider inside a bordered container, and as the
// "inactive" state on toggles — solid fill = on, dashed outline = off.
export const DASH = 'border-t-2 border-dashed border-[#0E2A20]';

// A card with the heavy left spine. This is the board's card signature.
export const CARD_SPINE = 'bg-white rounded-2xl border-2 border-l-[14px] border-[#0E2A20]';

// Controls. inline-flex + leading-none is what actually centres uppercase
// tracked text in a pill — padding alone leaves it riding high because the
// line-box is taller than the caps.
const BTN_BASE =
  'inline-flex items-center justify-center leading-none text-xs font-semibold uppercase ' +
  'tracking-[0.12em] px-5 py-3 rounded-full border-2 border-[#0E2A20] ' +
  'transition-transform active:translate-y-[2px]';
export const BTN_LIGHT = `${BTN_BASE} bg-white text-[#0E2A20] shadow-[0_4px_0_#0E2A20] active:shadow-[0_2px_0_#0E2A20]`;
export const BTN_DARK = `${BTN_BASE} bg-[#0E2A20] text-white shadow-[0_4px_0_#0E2A20] active:shadow-[0_2px_0_#0E2A20]`;
// A control that is present but not the point — back links, tertiary actions.
export const BTN_GHOST =
  'text-xs font-semibold uppercase tracking-[0.18em] text-[#5A5A5A] hover:text-[#0E2A20] transition-colors';

// Toggle pill, for range/filter rows. `on` decides fill-vs-dashed.
export function pill(on) {
  return `text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border-2 ${
    on ? 'bg-[#0E2A20] text-white border-[#0E2A20]' : 'border-dashed border-[#0E2A20] text-[#0E2A20] hover:bg-white'
  }`;
}

// Form fields, so inputs stop looking like a different product.
export const FIELD = 'text-sm border-2 border-[#0E2A20] rounded-full px-4 py-2 bg-white text-[#0E2A20]';
export const FIELD_BOX = 'text-sm border-2 border-[#0E2A20] rounded-xl px-4 py-2 bg-white text-[#0E2A20]';

// Type. Display runs at weight NORMAL even when large — the board's headline
// voice is big and light, never bold.
export const H1 = 'font-display font-normal text-[#0E2A20] text-5xl leading-none tracking-tight';
export const H2 = 'font-display font-normal text-[#0E2A20] text-2xl leading-none';
export const LABEL = 'text-[0.69rem] font-semibold uppercase tracking-[0.18em] text-[#5A5A5A]';
export const BODY_MUTED = 'text-sm text-[#5A5A5A]';

// Standard page shell width, so five pages do not each pick their own.
export const SHELL = 'max-w-6xl mx-auto px-6';
